//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { UnifiedCardData, formatDate, displayOrNA } from './UnifiedCardData';
import RelatedLinks from './RelatedLinks';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import CommitsIcon from '../../../icons/commit_search.svg';
import { chipColorForResult } from './UnifiedCardData';

const normalizeTimestamp = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

export const CommitCard: React.FC<{ card: UnifiedCardData }> = ({ card }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="mb-6 rounded-lg bg-[#f2f4f5] p-6 dark:bg-[#393939]"
      id="id-commit-card-container"
      data-testid="commit-card-container"
    >
      <div
        className="mb-4 flex items-start gap-3"
        id="id-commit-card-content"
        data-testid="commit-card-content"
      >
        <div
          className="mt-0.5 shrink-0"
          id="id-commit-card-icon-wrapper"
          data-testid="commit-card-icon-wrapper"
        >
          <div
            className="flex size-8 items-center justify-center rounded"
            id="id-commit-card-icon"
            data-testid="commit-card-icon"
          >
            <img
              src={CommitsIcon}
              alt="Commit icon"
              className="size-8 object-contain"
            />
          </div>
        </div>
        <div
          className="min-w-0 flex-1"
          id="id-commit-card-details"
          data-testid="commit-card-details"
        >
          <div
            className="mb-1 flex items-center gap-2"
            id="id-commit-card-title-row"
            data-testid="commit-card-title-row"
          >
            <span
              className="text-[12px] text-gray-500 dark:text-gray-400"
              id="id-commit-card-type-label"
              data-testid="commit-card-type-label"
            >
              {t('commit')}
            </span>
            <span
              className="text-[12px] text-gray-300 dark:text-gray-600"
              id="id-commit-card-title-separator"
              data-testid="commit-card-title-separator"
            >
              |
            </span>
            <span
              className="cursor-default text-[14px] font-medium text-[#0043ce] dark:text-brand-interactiveDark"
              id="id-commit-card-title"
              data-testid="commit-card-title"
            >
              {card.cardTitle}
            </span>
          </div>
          <div
            className="mt-1 text-[12px] text-gray-600 dark:text-gray-300"
            id="id-commit-card-metadata-primary"
            data-testid="commit-card-metadata-primary"
          >
            <span
              id="id-commit-card-created-label"
              data-testid="commit-card-created-label"
            >
              {t('created')}:
            </span>{' '}
            <span
              id="id-commit-card-created-value"
              data-testid="commit-card-created-value"
            >
              {(() => {
                const formatted = formatDate(normalizeTimestamp(card.created));
                return formatted === '--' ? t('not-available') : formatted;
              })()}
            </span>
            <span
              className="mx-1"
              id="id-commit-card-separator-1"
              data-testid="commit-card-separator-1"
            >
              |
            </span>
            <span
              id="id-commit-card-creator-label"
              data-testid="commit-card-creator-label"
            >
              {t('creator')}:
            </span>
            <span
              className="ml-1"
              id="id-commit-card-creator-value"
              data-testid="commit-card-creator-value"
            >
              {displayOrNA(card.owner, t)}
            </span>
            {/* <span className="mx-1">|</span>
            <span>{t('environment')}</span>
            <span className="ml-1">{card.environment}</span> */}
            <span
              className="mx-1"
              id="id-commit-card-separator-2"
              data-testid="commit-card-separator-2"
            >
              |
            </span>
            <span
              id="id-commit-card-loop-label"
              data-testid="commit-card-loop-label"
            >
              {t('loop')}:
            </span>
            <span
              className="ml-1"
              id="id-commit-card-loop-value"
              data-testid="commit-card-loop-value"
            >
              {displayOrNA(card.loopName, t)}
            </span>
            {card.result && (
              <>
                <span
                  className="mx-1"
                  id="id-commit-card-separator-3"
                  data-testid="commit-card-separator-3"
                >
                  |
                </span>
                <span
                  id="id-commit-card-result-label"
                  data-testid="commit-card-result-label"
                >
                  {t('result')}:
                </span>
                <span
                  className={`ml-1 inline-flex h-[18px] items-center rounded-full px-2 text-[11px] font-medium ${chipColorForResult(card.result)}`}
                  id="id-commit-card-result-value"
                  data-testid="commit-card-result-value"
                >
                  {card.result}
                </span>
              </>
            )}
          </div>
          <p className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">
            <span
              id="id-commit-card-source-label"
              data-testid="commit-card-source-label"
            >
              {t('source')}:
            </span>
            <span
              className="ml-1"
              id="id-commit-card-source-value"
              data-testid="commit-card-source-value"
            >
              {displayOrNA(card.source, t)}
            </span>
            <span
              className="mx-1"
              id="id-commit-card-separator-4"
              data-testid="commit-card-separator-4"
            >
              |
            </span>
            <span
              id="id-commit-card-repository-label"
              data-testid="commit-card-repository-label"
            >
              {t('repository')}:
            </span>
            <span
              className="ml-1"
              id="id-commit-card-repository-value"
              data-testid="commit-card-repository-value"
            >
              {displayOrNA(card.repository, t)}
            </span>
          </p>

          {/* Links Related - Accordion */}
          <div
            className="mt-4 border-t border-gray-300 pt-4 dark:border-gray-600"
            id="id-commit-card-links-section"
            data-testid="commit-card-links-section"
          >
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-2 text-[12px] text-[#0043ce] transition-colors hover:underline dark:text-brand-interactiveDark"
            >
              <span
                id="id-commit-card-links-label"
                data-testid="commit-card-links-label"
              >
                {t('links-related-result')}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded && <RelatedLinks card={card} primaryLabel="commit-url" />}
          </div>
        </div>
      </div>
    </div>
  );
};
